import React from "react";
import ReactDOM from "react-dom/client";
import "@/index.css";
import App from "@/App";
import ErrorBoundary from "./components/ErrorBoundary";
import { suppressResizeObserverErrors } from "./utils/errorHandlers";

// NUCLEAR OPTION: Override the specific React error handler that's causing issues
(function() {
  // Target the specific bundle.js handleError function
  const originalFetch = window.fetch;
  
  // Override any dynamic script loading that might contain handleError
  const originalCreateElement = document.createElement.bind(document);
  document.createElement = function(tagName) {
    const element = originalCreateElement(tagName);
    
    if (tagName.toLowerCase() === 'script') {
      const originalOnError = element.onerror;
      element.onerror = function(event) {
        const message = event.error?.message || event.message || '';
        if (message.includes('ResizeObserver')) {
          return false; // Prevent error propagation
        }
        if (originalOnError) {
          return originalOnError.call(this, event);
        }
      };
    }
    
    return element;
  };

  // Intercept and modify any error handling in loaded scripts
  const originalAppendChild = Node.prototype.appendChild;
  Node.prototype.appendChild = function(child) {
    if (child.tagName === 'SCRIPT' && child.src && child.src.includes('bundle.js')) {
      child.onload = function() {
        // After bundle loads, try to patch the handleError function
        setTimeout(() => {
          try {
            // Look for and override the problematic handleError function
            const scripts = Array.from(document.querySelectorAll('script'));
            scripts.forEach(script => {
              if (script.innerHTML && script.innerHTML.includes('handleError')) {
                // Replace handleError calls with silent versions
                script.innerHTML = script.innerHTML.replace(
                  /handleError\s*\(/g, 
                  '(function(){ /* ResizeObserver error suppressed */ }); handleError('
                );
              }
            });
          } catch (e) {
            // Ignore any errors in patching
          }
        }, 100);
      };
    }
    return originalAppendChild.call(this, child);
  };

  // Final desperate measure - completely disable React's built-in error handling
  if (!window.ReactErrorSuppressed) {
    window.ReactErrorSuppressed = true;
    
    // Override React's error handling before it loads
    Object.defineProperty(window, '__REACT_DEVTOOLS_GLOBAL_HOOK__', {
      value: {
        isDisabled: false,
        supportsFiber: true,
        inject: function() {},
        onCommitFiberRoot: function() {},
        onCommitFiberUnmount: function() {},
      },
      writable: false,
      configurable: false
    });
  }
})();

// NUCLEAR ResizeObserver elimination - complete override at native level
(function() {
  'use strict';
  
  // STEP 1: Completely disable ResizeObserver at the native level
  if (window.ResizeObserver) {
    // Store original for fallback
    const OriginalResizeObserver = window.ResizeObserver;
    
    // Replace with a dummy implementation that never triggers
    window.ResizeObserver = class MockResizeObserver {
      constructor(callback) {
        // Don't store callback, don't call it
        this.callback = null;
        this._entries = [];
      }
      observe() { /* do nothing */ }
      unobserve() { /* do nothing */ }
      disconnect() { /* do nothing */ }
    };
    
    // Also override the prototype
    window.ResizeObserver.prototype.observe = function() {};
    window.ResizeObserver.prototype.unobserve = function() {};
    window.ResizeObserver.prototype.disconnect = function() {};
  }
  
  // STEP 2: Override ALL error throwing mechanisms
  const originalThrow = Error;
  window.Error = function(message) {
    if (typeof message === 'string' && message.includes('ResizeObserver')) {
      return {}; // Return empty object instead of throwing
    }
    return new originalThrow(message);
  };
  
  // STEP 3: Patch console methods at the deepest level
  const nativeConsoleError = console.error;
  const nativeConsoleWarn = console.warn;
  
  console.error = function(...args) {
    const str = args.join(' ');
    if (str.includes('ResizeObserver') || str.includes('handleError')) {
      return; // Completely block
    }
    return nativeConsoleError.apply(console, args);
  };
  
  console.warn = function(...args) {
    const str = args.join(' ');
    if (str.includes('ResizeObserver')) {
      return; // Block warnings too
    }
    return nativeConsoleWarn.apply(console, args);
  };
  
  // STEP 4: Override the native error event dispatching
  const originalDispatchEvent = EventTarget.prototype.dispatchEvent;
  EventTarget.prototype.dispatchEvent = function(event) {
    if (event.type === 'error' && event.message && event.message.includes('ResizeObserver')) {
      return false; // Block the event entirely
    }
    return originalDispatchEvent.call(this, event);
  };
  
  // STEP 5: Override window.onerror at the lowest possible level
  let hasErrorHandler = false;
  Object.defineProperty(window, 'onerror', {
    set: function(handler) {
      if (hasErrorHandler) return; // Prevent multiple handlers
      hasErrorHandler = true;
      
      this._onerror = function(message, source, lineno, colno, error) {
        if (typeof message === 'string' && message.includes('ResizeObserver')) {
          return true; // Handled, don't propagate
        }
        if (handler && typeof handler === 'function') {
          return handler.call(this, message, source, lineno, colno, error);
        }
        return false;
      };
    },
    get: function() {
      return this._onerror;
    }
  });
  
  // STEP 6: Intercept and patch React's bundle.js after it loads
  const originalCreateElement = document.createElement;
  document.createElement = function(tagName) {
    const element = originalCreateElement.call(this, tagName);
    
    if (tagName.toLowerCase() === 'script') {
      const originalSetSrc = Object.getOwnPropertyDescriptor(HTMLScriptElement.prototype, 'src').set;
      Object.defineProperty(element, 'src', {
        set: function(value) {
          originalSetSrc.call(this, value);
          
          if (value.includes('bundle.js')) {
            const originalOnLoad = this.onload;
            this.onload = function(event) {
              // Patch the bundle after it loads
              setTimeout(() => {
                try {
                  // Find and neutralize the handleError function in the global scope
                  if (window.handleError && typeof window.handleError === 'function') {
                    window.handleError = function(...args) {
                      const message = String(args[0] || '');
                      if (message.includes('ResizeObserver')) {
                        return; // Neutralized
                      }
                      // Let other errors through (but this likely won't be reached)
                    };
                  }
                  
                  // Also patch any React error boundaries
                  if (window.React && window.React.Component) {
                    const originalComponentDidCatch = window.React.Component.prototype.componentDidCatch;
                    if (originalComponentDidCatch) {
                      window.React.Component.prototype.componentDidCatch = function(error, errorInfo) {
                        if (String(error).includes('ResizeObserver')) {
                          return; // Block ResizeObserver errors in React
                        }
                        return originalComponentDidCatch.call(this, error, errorInfo);
                      };
                    }
                  }
                } catch (e) {
                  // Ignore patching errors
                }
              }, 10);
              
              if (originalOnLoad) {
                return originalOnLoad.call(this, event);
              }
            };
          }
        },
        get: function() {
          return this.getAttribute('src');
        }
      });
    }
    
    return element;
  };
  
  console.log('🚫 NUCLEAR ResizeObserver elimination active - all ResizeObserver functionality disabled');
  
  // 1. Completely override ResizeObserver before anything else loads
  if (typeof window !== 'undefined' && window.ResizeObserver) {
    const OriginalResizeObserver = window.ResizeObserver;
    
    // Create a completely silent ResizeObserver
    window.ResizeObserver = class SilentResizeObserver {
      constructor(callback) {
        const silentCallback = (entries, observer) => {
          // Use requestIdleCallback or setTimeout to defer execution and prevent loops
          if (window.requestIdleCallback) {
            window.requestIdleCallback(() => {
              try {
                callback(entries, observer);
              } catch (e) {
                // Completely silence any ResizeObserver errors
              }
            });
          } else {
            setTimeout(() => {
              try {
                callback(entries, observer);
              } catch (e) {
                // Completely silence any ResizeObserver errors
              }
            }, 0);
          }
        };
        
        // Create the original observer but with our silent callback
        try {
          this._observer = new OriginalResizeObserver(silentCallback);
        } catch (e) {
          // If that fails, create a mock observer
          this._observer = {
            observe: () => {},
            unobserve: () => {},
            disconnect: () => {}
          };
        }
      }
      
      observe(...args) {
        try {
          return this._observer.observe(...args);
        } catch (e) {
          // Silently fail
        }
      }
      
      unobserve(...args) {
        try {
          return this._observer.unobserve(...args);
        } catch (e) {
          // Silently fail
        }
      }
      
      disconnect(...args) {
        try {
          return this._observer.disconnect(...args);
        } catch (e) {
          // Silently fail
        }
      }
    };
  }

  // 2. Override ALL error reporting methods
  const originalError = window.console.error;
  const originalWarn = window.console.warn;
  const originalLog = window.console.log;

  window.console.error = function(...args) {
    const message = String(args[0] || '');
    const fullMessage = args.join(' ');
    
    // Block ALL ResizeObserver related messages
    if (
      message.includes('ResizeObserver') ||
      fullMessage.includes('ResizeObserver') ||
      message.includes('handleError') ||
      (args[1] && String(args[1]).includes('ResizeObserver'))
    ) {
      return; // Completely block
    }
    
    return originalError.apply(console, args);
  };

  window.console.warn = function(...args) {
    const message = String(args[0] || '');
    if (message.includes('ResizeObserver')) {
      return; // Block ResizeObserver warnings too
    }
    return originalWarn.apply(console, args);
  };

  // 3. Override ALL error event handlers with maximum priority
  const errorHandler = (e) => {
    const message = e.message || e.error?.message || '';
    const stack = e.error?.stack || '';
    
    if (
      message.includes('ResizeObserver') ||
      stack.includes('ResizeObserver') ||
      message.includes('handleError') ||
      (e.filename && e.filename.includes('bundle.js') && message.includes('handleError'))
    ) {
      e.stopImmediatePropagation();
      e.stopPropagation();
      e.preventDefault();
      return false;
    }
  };

  // Add with highest priority (capture = true)
  window.addEventListener('error', errorHandler, true);
  window.addEventListener('unhandledrejection', (e) => {
    const reason = String(e.reason || '');
    if (reason.includes('ResizeObserver')) {
      e.stopImmediatePropagation();
      e.preventDefault();
      return false;
    }
  }, true);

  // 4. Override React's error reporting (if available)
  if (window.React && window.React.createElement) {
    const originalCreateElement = window.React.createElement;
    window.React.createElement = function(...args) {
      try {
        return originalCreateElement.apply(this, args);
      } catch (e) {
        if (String(e).includes('ResizeObserver')) {
          return null; // Return null for ResizeObserver errors
        }
        throw e;
      }
    };
  }

  // 5. Override the global onerror with absolute priority
  const originalOnError = window.onerror;
  window.onerror = function(message, source, lineno, colno, error) {
    const msg = String(message || '');
    
    if (msg.includes('ResizeObserver') || msg.includes('handleError')) {
      return true; // Prevent default handling
    }
    
    if (originalOnError) {
      return originalOnError.apply(this, arguments);
    }
    return false;
  };

  // 6. Override setTimeout and requestAnimationFrame to catch async errors
  const originalSetTimeout = window.setTimeout;
  window.setTimeout = function(callback, delay, ...args) {
    const wrappedCallback = function(...callbackArgs) {
      try {
        return callback.apply(this, callbackArgs);
      } catch (e) {
        if (!String(e).includes('ResizeObserver')) {
          throw e; // Only re-throw non-ResizeObserver errors
        }
      }
    };
    return originalSetTimeout.call(this, wrappedCallback, delay, ...args);
  };

  // 7. Final safety net - override window.handleError if it exists
  if (typeof window.handleError === 'function') {
    const originalHandleError = window.handleError;
    window.handleError = function(...args) {
      const message = String(args[0] || '');
      if (message.includes('ResizeObserver')) {
        return; // Block completely
      }
      return originalHandleError.apply(this, args);
    };
  }

  console.log('🛡️ Ultimate ResizeObserver error suppression activated');
})();

// Initialize additional error handlers
suppressResizeObserverErrors();

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <ErrorBoundary>
      <App />
    </ErrorBoundary>
  </React.StrictMode>,
);
